<template>
    <div
        v-if="isOpen"
        class="add-order-modal"
        @click="closeModal()">
        <div class="add-order-modal__container" @click.stop>
            <div class="divider-paper--top add-order-modal__divider__top"></div>
                <button class="add-order-modal__upper-close-btn" @click="closeModal()"><img src="@/assets/images/decoration/cross-shop.svg"></button>
                <div class="add-order-modal__content">
                    <div v-if="hasMultipleActiveSubscriptions && !addressConfirmed">
                        <h2 class="shop__heading" v-text="$t('SHOP_CONFIRMATION_ADDRESS_HEADING')"></h2>
                        <p class="shop__paragraph" v-text="`${$t('SHOP_CONFIRMATION_ADDRESS_TEXT')}`"></p>
                        <subscription-dropdown class="shop__multiselect add-order-modal__multiselect" :showLabels="false" label="" placeholderText="Address"/>
                        <button v-if="activeSubscription.status === 'Active'" class="add-order-modal__button " v-text="$t('SHOP_PRODUCT_ADD')" @click="updateOrder"></button>
                        <unpause-button
                            v-if="activeSubscription.status === 'Paused'"
                            class="mt-5"
                            :positionClass="'next-delivery-modal'"
                            :subscription="activeSubscription"/>
                    </div>
                    <div v-if="orderProcessed">
                        <h2 class="shop__heading" v-text="$t('SHOP_CONFIRMATION_HOORAY')"></h2>
                        <p class="shop__paragraph" v-if="!preOrderDate">
                            {{`${$t('SHOP_CONFIRMATION_ON_ITS_WAY_1')} ${item.title} ${$t('SHOP_CONFIRMATION_ON_ITS_WAY_2')}`}}
                            <span class="shop__paragraph add-order-modal__delivery">{{` ${deliveryDate}.`}}</span>
                        </p>
                        <p class="shop__paragraph" v-if="preOrderDate">
                            {{`${$t('SHOP_CONFIRMATION_ON_ITS_WAY_1')} ${item.title} ${$t('SHOP_CONFIRMATION_ON_ITS_WAY_3')}`}}
                            <span class="shop__paragraph add-order-modal__delivery">{{` ${preOrderDate}.`}}</span>
                        </p>
                        <recommendations
                            class="m-4"
                            :className="'next-delivery'"
                            :headerText="$t('RECOMMENDATIONS_ORDER_SUMMARY')"
                            :hideTitle="true"
                            :hidePrice="true"
                            :numItems="4"
                            :excludeIds="[item.productId]"/>
                        <router-link v-if="!isEarliestDate" :to="{name: 'account.delivery-schedule'}" class="shop__paragraph add-order-modal__link" v-text="$t('SHOP_CONFIRMATION_RECEIVE_SOONER')">
                        </router-link>
                    </div>
                    <div v-if="orderFailed">
                        <h2 class="shop__heading" v-text="$t('SHOP_ERROR_HEADING')"></h2>
                        <p class="shop__paragraph" v-text="$t('SHOP_ERROR_MESSAGE', {quantity: quantity, title: item.title})"></p>
                        <p class="shop__paragraph" v-text="errorMessage"></p>
                        <recommendations
                            class="m-4"
                            :className="'next-delivery'"
                            :headerText="$t('RECOMMENDATIONS_ORDER_SUMMARY')"
                            :hideTitle="true"
                            :hidePrice="true"
                            :numItems="4"
                            :excludeIds="[item.productId]"/>
                    </div>
                </div>
            <div class="divider-paper--bottom add-order-modal__divider__bottom"></div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as moment from 'moment';
import { formatDisplayDate } from '@/helpers/DateHelpers';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubscriptionDropdown from '@/components/partials/account/rate-my-flowers/SubscriptionDropdown';
import ShopService from '@/services/shop/ShopService.js';
import UnpauseButton from '@/components/partials/account/my-delivery-addresses/UnpauseButton.vue';
import Recommendations from '../../partials/account/shop/Recommendations.vue';

let shopService = new ShopService();
export default {
    name: 'AddToNextDeliveryModal',
    components: {
        Recommendations,
        SubscriptionDropdown,
        UnpauseButton,
    },
    data() {
        return {
            isOpen: true,
            errorHandler: new ErrorResponseHandler(),
            error: false,
            addressConfirmed: false,
            multipleAddressOrderSuccess: false,
        };
    },
    props: {
        item: {
            required: true,
            type: Object,
        },
        quantity: {
            required: true,
            type: Number,
        },
        orderSuccess: {
            type: Boolean,
            default: false,
        },
        parentErrorHandler: {
            required: false,
            type: Object,
        },
    },
    computed: {
        ...mapGetters({
            hasMultipleActiveSubscriptions: 'subscription/hasMultipleActiveSubscriptions',
            activeSubscription: 'subscription/getActiveSubscription',
            nextAvailableDelivery: 'shop/getNextAvailableDeliveryDate',
        }),
        orderProcessed() {
            return this.orderSuccess || this.multipleAddressOrderSuccess;
        },
        orderFailed() {
            return this.error || (!this.hasMultipleActiveSubscriptions && !this.orderSuccess);
        },
        deliveryDate() {
            return formatDisplayDate(this.nextAvailableDelivery);
        },
        isEarliestDate() {
            const deliveryDate = moment(this.nextAvailableDelivery);
            const nineDaysTime = moment().add(9, 'days');
            const isEarliestDate = deliveryDate.isSameOrBefore(nineDaysTime);
            return isEarliestDate;
        },
        preOrderDate() {
            if (!this.item.available_from) return '';
            let date = new Date(this.item.available_from);
            return moment(date).format('Do MMMM');
        },
        errorMessage() {
            if (this.error) {
                return this.errorHandler.response && this.errorHandler.response.error;
            } else if (!this.hasMultipleActiveSubscriptions && !this.orderSuccess) {
                return this.parentErrorHandler.response && this.parentErrorHandler.response.error;
            }
            return '';
        },
    },
    watch: {
        activeSubscription() {
            this.$store.dispatch('shop/getNextDeliveryDate');
        },
    },
    methods: {
        updateOrder() {
            this.addressConfirmed = true;
            shopService.updateOrder(this.activeSubscription.id, { product_id: this.item.productId, quantity: this.quantity })
                .then(() => {
                    this.multipleAddressOrderSuccess = true;
                    this.error = false;
                    this.$store.dispatch('shop/fetchOrderSummary', true);
                })
                .catch(error => {
                    this.multipleAddressOrderSuccess = false;
                    this.error = true;
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                });
        },
        closeModal() {
            this.isOpen = false;
            this.$emit('closed');
        },
    },
};
</script>
